import type { MinigameTurnoverRequirement } from '~/types/minigame-turnover';

// Mapeador snake_case (core) → camelCase do requisito mínimo de turnover de D-1
// (ENG-820).
//
// Vive fora de `api-roulette` e de `api-daily-checkin` porque o core manda o
// MESMO bloco nas três superfícies (lista e detalhe da roleta, board do
// check-in). Um mapeador por adapter seria a mesma leitura escrita duas vezes —
// e é justamente onde os defaults divergiriam.

/**
 * Sempre devolve um objeto, nunca null: o bloco vem em todas as respostas, e um
 * back mais antigo (ou um payload truncado) tem de virar "não se aplica" —
 * nunca um bloqueio inventado.
 */
export function mapTurnoverRequirement(raw: any): MinigameTurnoverRequirement {
  const asNumber = (value: unknown): number =>
    value == null || !Number.isFinite(Number(value)) ? 0 : Number(value);

  return {
    // Ausente = não se aplica. É o default seguro nesta direção: um
    // falso-negativo esconde o painel de quem está bloqueado (e o servidor
    // recusa a ação de qualquer forma, porque a regra vive lá), enquanto um
    // falso-positivo travaria a tela de quem tem direito a jogar.
    required: raw?.required === true,
    met: raw?.met === true,
    // decimal(12,2) do Laravel pode chegar como string ("200.00").
    minAmount: asNumber(raw?.min_amount),
    achievedAmount: asNumber(raw?.achieved_amount),
    day: raw?.day ?? null,
    // Só string não vazia vira copy personalizada: um back antigo, um payload
    // truncado ou uma string em branco têm de cair no texto padrão da tela, e
    // não deixar o jogador sem explicação nenhuma.
    title:
      typeof raw?.title === 'string' && raw.title.trim() !== ''
        ? raw.title
        : null,
    description:
      typeof raw?.description === 'string' && raw.description.trim() !== ''
        ? raw.description
        : null,
    // Ausente = false: o aviso é uma afirmação sobre o estado da rotina, e um
    // payload truncado não pode inventá-la.
    collecting: raw?.collecting === true,
    scope: {
      // Ausente = false: "vale em qualquer jogo" é uma afirmação, e um payload
      // truncado não pode virar isso por omissão.
      all_games: raw?.scope?.all_games === true,
      categories: Array.isArray(raw?.scope?.categories)
        ? raw.scope.categories.filter(
            (name: unknown): name is string => typeof name === 'string',
          )
        : [],
      // Ausente = 0: sem contagem não há lista para oferecer, e o botão de ver
      // os jogos simplesmente não aparece.
      gamesCount: asNumber(raw?.scope?.games_count),
      requirementId:
        raw?.scope?.requirement_id == null
          ? null
          : Number(raw.scope.requirement_id),
    },
  };
}
